<template>
  <div v-if="store">
    <ShowPageHeader :backLabel="t('back')" backRoute="/stores" :title="store.salesChannel || '—'">
      <template #badges>
        <span
          v-if="store.organization?.name"
          class="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800"
        >
          {{ store.organization.name }}
        </span>
        <FieldSpan translation-prefix="store.status_label" :style-to-add="statusBadgeClass(status)" :value="status" />
      </template>
      <template #actions>
        <SecondaryButton data-test="edit-monthly-sales-target" @click="monthlySalesTargetModalOpen = true">
          {{ t("edit_monthly_sales_target") }}
        </SecondaryButton>
      </template>
    </ShowPageHeader>

    <div class="mt-6 bg-white rounded-2xl shadow-md p-6">
      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-4">
        <StackedField :label="t('id')" :value="String(store.id)" />
        <StackedField :label="t('external_id')" :value="store.externalId" />
        <StackedField :label="t('organization')" :value="store.organization?.name" />
        <StackedField :label="t('business_name')" :value="store.businessName" />
        <StackedField :label="t('sales_channel')" :value="sourceLabel" />
        <StackedField :label="t('ownership_type')" :value="ownershipTypeLabel" />
        <StackedField
          class="sm:col-span-2"
          :label="t('shipping_address')"
          :value="formatLocation(store.shippingAddress)"
        />
        <StackedField v-if="salesManagerName" :label="t('sales_manager')" :value="salesManagerName" />
        <StackedField :label="t('monthly_sales_target')" :value="store.monthlySalesTarget" />
      </div>
    </div>

    <TabsNavigation :tabs="tabs" class="my-6" />

    <StoreReferentInformation v-if="currentTab === 'referent'" :referent="store.referent" />
    <StoreBusinessVerticals v-if="currentTab === 'businessVerticals'" :storeId="store.id" />

    <StoreMonthlySalesTargetModal
      :open="monthlySalesTargetModalOpen"
      :storeId="store.id"
      :currentMonthlySalesTarget="store.monthlySalesTarget"
      @close="monthlySalesTargetModalOpen = false"
      @updated="onMonthlySalesTargetUpdated"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { useI18n } from "vue-i18n";
import { FieldSpan, SecondaryButton, ShowPageHeader, StackedField, TabsNavigation, formatLocation } from "@estaly/ui";
import StoreBusinessVerticals from "@/components/stores/StoreBusinessVerticals.vue";
import StoreMonthlySalesTargetModal from "@/components/stores/StoreMonthlySalesTargetModal.vue";
import StoreReferentInformation from "@/components/stores/StoreReferentInformation.vue";

const SHOW_INCLUDES = ["organization", "referent", "sales_manager"];

export default {
  name: "StoreDetails",

  components: {
    FieldSpan,
    SecondaryButton,
    ShowPageHeader,
    StackedField,
    TabsNavigation,
    StoreBusinessVerticals,
    StoreMonthlySalesTargetModal,
    StoreReferentInformation,
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  data() {
    return {
      store: null,
      monthlySalesTargetModalOpen: false,
    };
  },

  computed: {
    ...mapGetters("stores", ["statusOrSetup", "statusBadgeClass"]),

    status() {
      return this.statusOrSetup(this.store);
    },

    sourceLabel() {
      return this.store.source ? this.t(`store.source.${this.store.source}`) : null;
    },

    ownershipTypeLabel() {
      return this.store.ownershipType ? this.t(`ownership_types.${this.store.ownershipType}`) : null;
    },

    salesManagerName() {
      const salesManager = this.store.salesManager;
      if (!salesManager) return null;

      return [salesManager.firstName, salesManager.lastName].filter(Boolean).join(" ");
    },

    currentTab() {
      return this.$route.query.tab || "referent";
    },

    tabs() {
      return [
        { name: "referent", displayName: this.t("tab_referent"), query: { tab: "referent" } },
        {
          name: "businessVerticals",
          displayName: this.t("tab_business_verticals"),
          query: { tab: "businessVerticals" },
        },
      ];
    },
  },

  async mounted() {
    await this.getStore();
  },

  methods: {
    ...mapActions("stores", { fetchStore: "fetch" }),

    formatLocation,

    async getStore() {
      const store = await this.fetchStore({ id: this.$route.params.id, includes: SHOW_INCLUDES });
      if (!store) return;

      this.store = store;
    },

    async onMonthlySalesTargetUpdated() {
      this.monthlySalesTargetModalOpen = false;
      await this.getStore();
    },
  },
};
</script>

<i18n src="./StoreDetails.i18n.json"></i18n>
